/**
 * Hand-reported client errors → PostHog Error Tracking.
 *
 * Autocapture (`capture_exceptions` in ./client) already covers unhandled
 * errors and rejections. This is the path for errors React swallows into an
 * error boundary, which never reach `window.onerror` - the route-level
 * `error.tsx` files and `PanelErrorBoundary` call it.
 *
 * ## What is attached, and what never is
 *
 * The property shape is FIXED and id-only:
 *
 *   { surface, route, graph_id?, panel?, app_version }
 *
 * Wire properties are snake_case, matching the backend (`reportError`) and the
 * desktop main process (`reportDesktopError`), so one PostHog issue list slices
 * across every surface without per-surface property aliases. Call sites keep
 * camelCase TypeScript context (`graphId`); the conversion happens in exactly
 * one place, `buildClientErrorProperties`.
 *
 * Nothing derived from user content is added: no message bodies, no note text,
 * no titles, no emails, no tokens. The exception's own message/stack is sent by
 * posthog-js (that is the point of error tracking), but every property WE
 * attach is scrubbed first - the route drops its query string and hash, any
 * path segment that looks like an email or a credential is redacted, and every
 * id-shaped segment collapses to a placeholder so `route` is a route PATTERN
 * (`/spaces/:spaceId/chat/:param`) rather than a filled path. Space slugs are
 * derived from customer space names, so a filled path names the account.
 */

import packageJson from '../../../../package.json';

import { isDesktopShell } from '@naumu/shared/native/is-native-shell';

import { isDevMode, withPostHog } from './client';
import {
	PRE_FILTERED_EXCEPTION_PROPERTY,
	shouldIgnoreClientError,
} from './ignore-client-errors';

/** Which shell the crash happened in. The desktop app renders this same web build. */
export type ClientErrorSurface = 'web' | 'desktop-renderer';

/** Caller-supplied context. Every field is optional and every field is scrubbed. */
export interface ClientErrorContext {
	/** Path the user was on. Defaults to `location.pathname`. Query/hash stripped. */
	route?: string;
	/** Active space id (a UUID). Dropped unless it is id-shaped. */
	graphId?: string;
	/** Paneling island id, e.g. `chat` or `node-details`. Dropped unless id-shaped. */
	panel?: string;
}

/**
 * The exact property bag sent with every `$exception` we report by hand.
 *
 * snake_case on purpose - these are PostHog wire names, not TypeScript. They
 * must match the other reporters key for key (`surface`, `app_version`,
 * `graph_id`), so a breakdown by `surface` works across web, backend and
 * desktop in a single insight.
 */
export interface ClientErrorProperties {
	surface: ClientErrorSurface;
	route: string;
	graph_id?: string;
	panel?: string;
	app_version: string;
}

const UNKNOWN_ROUTE = 'unknown';
const MAX_ROUTE_LENGTH = 200;

/** Ids we accept verbatim: uuids, slugs, panel names. Nothing with an `@` or a dot. */
const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

const EMAIL_PATTERN = /[^\s/]+@[^\s/]+\.[^\s/]+/;
/** Long opaque strings are treated as credentials: jwt parts, api keys, hex digests. */
const CREDENTIAL_PATTERN = /^[A-Za-z0-9._~+/=-]{40,}$/;

/** Placeholder for a path segment the heuristic could not name. */
const GENERIC_PARAM = ':param';

/**
 * Segments that are an identifier rather than part of the route shape.
 *
 * A space slug is derived from the customer's own space name
 * (`acme-corp-0464cbfa`), so a filled path is customer data, not a route. These
 * catch the id shapes the app actually mints, and every one of them requires a
 * DIGIT so no literal route segment can match: not one of the ~60 static
 * segments under `app/` contains a digit, and neither do the panel ids.
 */
const PARAM_SEGMENT_PATTERNS = [
	// Canonical uuid: graph ids, node ids, thread ids.
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
	// Bare numeric segment.
	/^\d+$/,
	// Bare hex digest, e.g. a short id or a content hash.
	/^(?=[0-9a-f]*\d)[0-9a-f]{8,}$/i,
	// Slug with a hex suffix - the shape every space slug takes.
	/-(?=[0-9a-f]*\d)[0-9a-f]{6,}$/i,
	// Long opaque mixed id (cuid / nanoid / `thread-1787...`).
	/^(?=[A-Za-z0-9_-]*\d)[A-Za-z0-9_-]{12,}$/,
];

/**
 * True when the segment is an id, so it must become a placeholder.
 *
 * Segments that are already a placeholder (`:spaceId`, produced upstream by
 * `buildRoutePattern`) are left alone - they carry no data and naming them
 * twice would flatten a useful pattern back to `:param`.
 */
const isParamSegment = (segment: string): boolean => {
	if (!segment || segment.startsWith(':')) return false;
	return PARAM_SEGMENT_PATTERNS.some((pattern) => pattern.test(segment));
};

/**
 * The app version this bundle was built from - the same value PostHog carries
 * as the `app_version` super property, so an issue can be pinned to a release.
 * `NEXT_PUBLIC_APP_VERSION` overrides it for builds that version independently
 * of the monorepo root; the sourcemap upload script resolves it the same way.
 */
const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION || packageJson.version;

const scrubSegment = (segment: string): string => {
	if (!segment) return segment;
	if (EMAIL_PATTERN.test(segment)) return 'redacted';
	if (CREDENTIAL_PATTERN.test(segment)) return 'redacted';
	if (isParamSegment(segment)) return GENERIC_PARAM;
	return segment;
};

/**
 * Reduce a URL or path to a bare, content-free ROUTE PATTERN.
 *
 * Query strings and fragments are dropped wholesale - they are where invite
 * tokens, reset tokens, `?email=`, and search text live. Remaining segments are
 * redacted individually so an email or token embedded in the path (e.g.
 * `/accept-invite/<token>`) never ships.
 *
 * A FILLED path is customer data: space slugs are derived from the customer's
 * own space name, so `/spaces/acme-corp-0464cbfa/chat` names an account. Every
 * id-shaped segment is therefore collapsed to a placeholder. Callers that can
 * see Next's segment info should hand in `buildRoutePattern()` output instead,
 * which names the placeholders (`:spaceId`); this heuristic is the floor for
 * the boundaries that cannot (the root boundary, class components).
 */
export const scrubRoute = (route?: string): string => {
	const raw =
		route ??
		(typeof window !== 'undefined' ? window.location.pathname : undefined);
	if (!raw) return UNKNOWN_ROUTE;

	const withoutHash = raw.split('#')[0] ?? '';
	const pathOnly = withoutHash.split('?')[0] ?? '';
	// Absolute URLs arrive from callers that pass `location.href`; keep the path.
	const path = pathOnly.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '');
	if (!path) return UNKNOWN_ROUTE;

	const scrubbed = path
		.split('/')
		.map(scrubSegment)
		.join('/')
		.slice(0, MAX_ROUTE_LENGTH);

	return scrubbed || UNKNOWN_ROUTE;
};

/** The shape `useParams()` returns: scalar segments, plus arrays for catch-alls. */
export type RouteParamValues = Record<string, string | string[] | undefined>;

/**
 * Turn a filled pathname into a NAMED route pattern using Next's own params.
 *
 * `usePathname()` alone is a filled path - `/spaces/acme-corp-0464cbfa/chat/
 * thread-1787f0` - and every dynamic segment in it is customer data. `useParams()`
 * tells us exactly which values were substituted, so each one is swapped back for
 * its `:name`. Whatever is left goes through `scrubRoute`, whose id heuristic
 * catches the segments params cannot name: a catch-all (`[[...panel]]`) reports
 * one param holding many segments, and collapsing all of them to `:panel` would
 * throw away the part that is route shape (`chat`), so those fall through to the
 * heuristic and come back as `chat/:param`.
 *
 * Both the decoded and the encoded form of each value are matched, because
 * `useParams()` decodes and `usePathname()` does not.
 */
export const buildRoutePattern = (
	pathname?: string | null,
	params?: RouteParamValues,
): string => {
	if (!pathname) return scrubRoute(pathname ?? undefined);

	const named = new Map<string, string>();
	for (const [name, value] of Object.entries(params ?? {})) {
		// Catch-all params hold an array of segments with one shared name; naming
		// them would erase the literal segments among them.
		if (typeof value !== 'string' || !value) continue;
		named.set(value, `:${name}`);
		try {
			named.set(encodeURIComponent(value), `:${name}`);
		} catch {
			// A lone surrogate cannot be encoded; the raw form is already indexed.
		}
	}

	const patterned = pathname
		.split('/')
		.map((segment) => named.get(segment) ?? segment)
		.join('/');

	return scrubRoute(patterned);
};

/** Keep an id only when it really is an id. Anything else is dropped, not redacted. */
export const scrubId = (value?: string): string | undefined => {
	if (!value) return undefined;
	return ID_PATTERN.test(value) ? value : undefined;
};

/**
 * Build the fixed, scrubbed property bag. Exported for the boundary tests.
 *
 * This is the ONE camelCase-to-snake_case boundary: callers hand in a
 * `ClientErrorContext` in ordinary TypeScript casing and get back the exact
 * wire shape. Do not convert anywhere else, and do not let a call site pass a
 * pre-snaked key.
 */
export const buildClientErrorProperties = (
	ctx: ClientErrorContext = {},
): ClientErrorProperties => {
	const graphId = scrubId(ctx.graphId);
	const panel = scrubId(ctx.panel);

	return {
		surface: isDesktopShell() ? 'desktop-renderer' : 'web',
		route: scrubRoute(ctx.route),
		...(graphId ? { graph_id: graphId } : {}),
		...(panel ? { panel } : {}),
		app_version: APP_VERSION,
	};
};

/**
 * Report a caught client error to PostHog Error Tracking.
 *
 * Silent no-op when analytics is disabled (dev, no API key, PostHog failed to
 * load) and when the error is on the ignore list. Never throws - a reporter
 * that crashes inside an error boundary would take the fallback UI with it.
 */
export const reportClientError = (
	err: unknown,
	ctx: ClientErrorContext = {},
): void => {
	try {
		if (isDevMode()) return;
		if (shouldIgnoreClientError(err)) return;

		const properties = buildClientErrorProperties(ctx);
		// Queued and replayed if posthog-js is still in flight, so a crash during
		// boot - the most interesting kind - is not the one we lose.
		withPostHog((posthog) => {
			posthog.captureException(err, {
				...properties,
				// captureException runs through capture(), which invokes
				// before_send - and the ignore list was ALREADY applied above.
				// This tells the hook to pass the event through instead of
				// re-judging it; the hook deletes the key before it ships.
				[PRE_FILTERED_EXCEPTION_PROPERTY]: true,
			});
		});
	} catch {
		// Reporting must never be the thing that breaks the page.
	}
};
